// eslint-disable-next-line no-restricted-imports
import { useTheme } from '@emotion/react';
import { Button, Flex, Typography } from '@iheartjane/reefer';
import { useCallback, useMemo } from 'react';
import { QRCode } from 'react-qrcode-logo';

import type { RootLoaderType } from '@jane/kiosk/data-access';
import { getLocalCartUuid } from '@jane/kiosk/data-access';
import { useTypedRouteLoaderData } from '@jane/kiosk/hooks';
import {
  useProductTracking,
  useResetKiosk,
  useSessionContext,
} from '@jane/kiosk/providers';
import { buildKioskMobileUrl } from '@jane/kiosk/util';

/**
 * @todo
 * We want this to be an SVG that we can manipulate,
 * but Chrome & Safari both seem to render an SVG to the
 * QR canvas with a tiny border around the image. The cause
 * of this is unclear.
 */
import qrPng from '../assets/qr-logo.png';

// eslint-disable-next-line @iheartjane/workspace/no-dom-globals-in-module-scope
const qrWidth = window.outerWidth * 0.22;

export const QrCheckout = () => {
  const { storeId } = useTypedRouteLoaderData<RootLoaderType>('root');
  const {
    cartToppers: { cartTopperIds },
    inlineAds: { inlineAdIds },
  } = useProductTracking();

  const cartUuid = getLocalCartUuid();
  const { resetKiosk } = useResetKiosk();
  const { sessionId, jdid } = useSessionContext();

  const { colors } = useTheme();

  const mobileCartUrl = useMemo(
    () =>
      buildKioskMobileUrl(storeId, `cart/${cartUuid}`, {
        sessionId,
        jdid,
        topperIds: cartTopperIds.join(','),
        inlineAds: inlineAdIds.join(','),
      }),
    [cartUuid, jdid, sessionId, storeId, cartTopperIds, inlineAdIds]
  );

  const returnHome = useCallback(() => {
    // Treat like order submit success & reset the kiosk but don't delete the cart
    resetKiosk({ shouldDeleteCart: false });
  }, [resetKiosk]);

  return (
    <Flex
      background="primary"
      alignItems="center"
      flexDirection="column"
      justifyContent="center"
      width="100%"
      height="100%"
    >
      <Flex
        background="grays-white"
        borderRadius="lg"
        p={24}
        ariaLabel="QR Code"
        data-cart-url={mobileCartUrl}
        role="region"
      >
        <QRCode
          id="mobile-checkout-qr"
          fgColor={colors.grays.black}
          logoImage={qrPng}
          logoWidth={qrWidth * 0.3}
          size={qrWidth}
          value={mobileCartUrl}
        />
      </Flex>
      <Typography
        color="text-info"
        mb={24}
        mt={96}
        variant="h2-regular"
        as="h2"
      >
        Just one more step to go
      </Typography>
      <Typography
        color="text-info"
        variant="h1-regular"
        as="h1"
        textAlign="center"
      >
        Scan to complete
      </Typography>
      <Typography
        color="text-info"
        variant="h1-regular"
        as="h1"
        textAlign="center"
      >
        your order
      </Typography>

      <Flex mt={96} minWidth="18%">
        <Button
          ariaLabel="Done, qr scanned"
          full
          label="Done"
          size="jumbo"
          onClick={returnHome}
          variant="secondary"
        />
      </Flex>
    </Flex>
  );
};
